import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js';
import { EditorView } from '@codemirror/view';
import {
  state, setDoc, setMode, setTemplate, togglePalette, hideModal, hydrate, pushToast, newFile, collab, sidebarOpen, toggleSidebar, toggleNotifCenter, peers, importFiles,
} from './state';
import { NotificationCenter } from './ui/NotificationCenter';
import { Welcome } from './pages/Welcome';
import { Editor } from './editor/Editor';
import { EditorToolbar } from './editor/EditorToolbar';
import { Preview } from './preview/Preview';
import { bindScrollSync } from './sync/scrollSync';
import { Toolbar } from './ui/Toolbar';
import { StatusBar } from './ui/StatusBar';
import { Sidebar } from './ui/Sidebar';
import { Toasts } from './ui/Toasts';
import { Modals } from './ui/Modals';
import { Palette, type Command } from './commands/Palette';
import { parseFrontmatter } from './preview/pipeline';
import { TEMPLATES, isTemplateId } from './templates/registry';
import { formatTables } from './editor/formatTables';

export function App() {
  // Welcome route is a static landing page, bypasses the whole editor app.
  if (typeof location !== 'undefined') {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/welcome') return <Welcome />;
  }
  const [view, setView] = createSignal<EditorView | null>(null);
  const [previewEl, setPreviewEl] = createSignal<HTMLDivElement | null>(null);
  let lastFmTemplate: string | null = null;

  onMount(() => {
    void hydrate();
  });

  // Frontmatter drives template (only when the value changes)
  createEffect(() => {
    if (!state.doc) return;
    const { frontmatter } = parseFrontmatter(state.doc);
    const raw = frontmatter.template;
    const next = typeof raw === 'string' && isTemplateId(raw) ? raw : null;
    if (next !== lastFmTemplate) {
      lastFmTemplate = next;
      if (next) setTemplate(next);
    }
  });

  // Bind scroll sync in split mode (re-binds when editor or preview node changes)
  createEffect(() => {
    if (state.mode !== 'split') return;
    const v = view();
    const p = previewEl();
    if (!v || !p) return;
    const timer = setTimeout(() => {
      const unbind = bindScrollSync(v, p);
      onCleanup(unbind);
    }, 50);
    onCleanup(() => clearTimeout(timer));
  });

  const replaceLines = (startLine: number, endLine: number, text: string) => {
    const v = view();
    if (v) {
      const cm = v.state.doc;
      const total = cm.lines;
      const from = cm.line(Math.max(1, Math.min(startLine, total))).from;
      const to = cm.line(Math.max(1, Math.min(endLine, total))).to;
      v.dispatch({ changes: { from, to, insert: text } });
    } else {
      const lines = state.doc.split('\n');
      const before = lines.slice(0, startLine - 1);
      const after = lines.slice(endLine);
      setDoc([...before, text, ...after].join('\n'));
    }
  };

  const handleFormatTables = () => {
    const v = view();
    const current = v ? v.state.doc.toString() : state.doc;
    const next = formatTables(current);
    if (next === current) return;
    if (v) {
      const sel = v.state.selection.main;
      v.dispatch({
        changes: { from: 0, to: v.state.doc.length, insert: next },
        selection: { anchor: Math.min(sel.anchor, next.length), head: Math.min(sel.head, next.length) },
      });
    } else {
      setDoc(next);
    }
    pushToast({ severity: 'success', title: 'Tables formatted' });
  };

  const commands = createMemo<Command[]>(() => [
    { id: 'file.new', label: 'New file', hint: 'Cmd-N', run: () => void newFile() },
    { id: 'file.import', label: 'Import files from disk…', run: () => void importFiles() },
    { id: 'edit.formatTables', label: 'Format tables', hint: '⌘⌥T', run: handleFormatTables },
    { id: 'view.toggleSidebar', label: 'Toggle sidebar', hint: '⌘\\', run: () => { toggleSidebar(); } },
    { id: 'view.notifications', label: 'Show notifications', run: () => { toggleNotifCenter(true); } },
    { id: 'view.edit', label: 'View: Edit only', hint: 'Cmd-1', run: () => setMode('edit') },
    { id: 'view.split', label: 'View: Split', hint: 'Cmd-2', run: () => setMode('split') },
    { id: 'view.preview', label: 'View: Preview only', hint: 'Cmd-3', run: () => setMode('preview') },
    ...TEMPLATES.map((t) => ({
      id: `template.${t.id}`,
      label: `Template: ${t.name}`,
      hint: t.description,
      run: () => setTemplate(t.id),
    })),
  ]);

  // Global shortcuts
  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'k') { e.preventDefault(); togglePalette(); }
      else if (e.key === 'n') { e.preventDefault(); void newFile(); }
      else if (e.key === '1') { e.preventDefault(); setMode('edit'); }
      else if (e.key === '2') { e.preventDefault(); setMode('split'); }
      else if (e.key === '3') { e.preventDefault(); setMode('preview'); }
      else if (e.altKey && e.code === 'KeyT') { e.preventDefault(); handleFormatTables(); }
      else if (e.key === '\\') { e.preventDefault(); toggleSidebar(); }
      else if (e.key === 'Escape' && state.modal) { e.preventDefault(); hideModal(); }
    };
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  // Warn before unload when you're alone in a room
  onMount(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.openKind === 'room' && peers().length === 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    onCleanup(() => window.removeEventListener('beforeunload', onBeforeUnload));
  });


  // Hash routing: open a room if URL has #room=...
  onMount(() => {
    const apply = async () => {
      const m = location.hash.match(/#room=([\w-]+)/);
      if (m && state.hydrated) {
        const existing = state.rooms.find((r) => r.roomId === m[1]);
        if (existing) {
          const { openItem } = await import('./state');
          await openItem('room', existing.id);
        } else {
          const { joinRoomByUrl } = await import('./state');
          await joinRoomByUrl(location.href);
        }
      }
    };
    const onHash = () => void apply();
    window.addEventListener('hashchange', onHash);
    // Defer initial application until hydration completes
    const check = setInterval(() => {
      if (state.hydrated) { clearInterval(check); void apply(); }
    }, 50);
    onCleanup(() => { window.removeEventListener('hashchange', onHash); clearInterval(check); });
  });

  return (
    <div class={`app mode-${state.mode}${sidebarOpen() ? '' : ' sidebar-closed'}`}>
      <div class="sidebar-backdrop" onClick={() => toggleSidebar()} />
      <Sidebar />
      <div class="main">
        <Toolbar />
        <main class="panes">
          <Show when={state.mode === 'edit' || state.mode === 'split'}>
            <section class="pane pane-edit">
              <EditorToolbar view={view()} />
              <Show keyed when={state.openId && `${state.openKind}:${state.openId}`}>
                {(_key) => (
                  <Editor
                    value={state.doc}
                    onChange={setDoc}
                    onView={setView}
                    collab={state.openKind === 'room' ? collab() : null}
                  />
                )}
              </Show>
            </section>
          </Show>
          <Show when={state.mode === 'preview' || state.mode === 'split'}>
            <section class="pane pane-preview">
              <Preview
                doc={state.doc}
                template={state.template}
                onPreviewRef={setPreviewEl}
                onReplaceLines={replaceLines}
              />
            </section>
          </Show>
        </main>
        <StatusBar />
      </div>
      <Palette open={state.paletteOpen} commands={commands()} onClose={() => togglePalette(false)} />
      <Modals />
      <NotificationCenter />
      <Toasts />
    </div>
  );
}
